import { useState } from 'react'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { useGetExercises } from 'redux/customHooks/useGetExercises'

import BrandColorButton from 'components/Buttons/BrandColorButton'
import OutlineButton from 'components/Buttons/OutlineButton'

 export default function BulkEditorExerciseCreateModalRouteView({ 
    setExerciseOverrideValue,
    setModalRoute 
}) {

    const { refetch } = useGetExercises()

    const [formData, setFormData] = useState({
        exerciseName: '',
        exerciseType: 'bodyweight',
        exerciseLateral: 'bilateral',
        exerciseVideo: null,
        exerciseThumbnail: null,
        exerciseInstructions: ''
    });

    const [validation, setValidation] = useState({
        exerciseName: true,
        exerciseVideo: true,
        exerciseThumbnail: true,
        exerciseInstructions: true,
        exerciseLateral: true,
        exerciseType: true
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const HandleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        setValidation(prev => ({
            ...prev,
            [field]: true
        }));
    };

    const ConvertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            if (!file) {
                resolve(null);
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = error => reject(error);
        });
    };

    const ValidateForm = () => {
        const newValidation = {
            exerciseName: formData.exerciseName.length > 0 && formData.exerciseName.length <= 100,
            exerciseVideo: formData.exerciseVideo === null || (formData.exerciseVideo.size <= 5 * 1024 * 1024),
            exerciseThumbnail: formData.exerciseThumbnail === null || (formData.exerciseThumbnail.size <= 1 * 1024 * 1024),
            exerciseInstructions: formData.exerciseInstructions.length <= 500,
            exerciseLateral: true,
            exerciseType: true
        };

        setValidation(newValidation);

        return Object.values(newValidation).every(v => v);
    };

    const OnCreateExercise = async () => {

        if (!ValidateForm()) {
            return;
        }

        try {

            setIsSubmitting(true);

            const videoBase64 = formData.exerciseVideo ? await ConvertFileToBase64(formData.exerciseVideo) : '';
            const imageBase64 = formData.exerciseThumbnail ? await ConvertFileToBase64(formData.exerciseThumbnail) : '';

            const jsonBody = {
                exerciseName: formData.exerciseName,
                exerciseType: formData.exerciseType,
                exerciseLateral: formData.exerciseLateral,
                exerciseVideo: videoBase64,
                exerciseThumbnail: imageBase64,
                exerciseInstructions: formData.exerciseInstructions || ''
            };

            // console.log(jsonBody);

            const response = await WhichWeightAPI('CORE', 'POST', '/exercise/custom', jsonBody);

            // console.log(response);

            if (response.data.status === "failure") {
                setIsSubmitting(false);
                console.error(response);
                return;
            }

            // Refetch exercises from backend to update redux cache
            
            await refetch();

            // Find the newly created exercise by ID

            const newExercise = response.data.data;

            if (!newExercise) {
                setIsSubmitting(false);
                console.error('Failed to find newly created exercise');
                return;
            }

            // console.log(newExercise);

            setExerciseOverrideValue(newExercise);

            setIsSubmitting(false);
            
            setModalRoute('override-input');

        } catch (error) {
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const OnCancelButton = () => {
        setModalRoute('exercise-list')
    }

    return (
        <div className="space-y-4">
            <div className='-mt-5 space-y-1'>
                <div className="text-xl text-left text-base font-semibold leading-6 text-gray-900">
                    Create Custom Exercise
                </div>

                <p className='text-sm text-left'>
                    Only available for Kinesis internal org
                </p>
            </div>

            <div>
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Exercise Name*
                </label>

                <div className="flex-1">
                    <input
                        type="text"
                        placeholder="Enter exercise name here"
                        value={formData.exerciseName}
                        onChange={(e) => HandleInputChange('exerciseName', e.target.value)}
                        className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        maxLength={100}
                        required
                    />

                    {!validation.exerciseName && (
                        <div className="mt-1 text-xs text-red-400">
                            Exercise name is required and must be 100 characters or less
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Type*
                    </label>

                    <select
                        value={formData.exerciseType}
                        onChange={(e) => HandleInputChange('exerciseType', e.target.value)}
                        className="mt-1 block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        required
                    >
                        <option value="bodyweight">Bodyweight</option>
                        <option value="recommendation">Recommendation</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Lateral*
                    </label>

                    <select
                        value={formData.exerciseLateral}
                        onChange={(e) => HandleInputChange('exerciseLateral', e.target.value)}
                        className="mt-1 block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        required
                    >
                        <option value="bilateral">Bilateral</option>
                        <option value="unilateral">Unilateral</option>
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">
                    Text Instructions
                </label>

                <textarea
                    placeholder="Enter text instructions here"
                    value={formData.exerciseInstructions}
                    onChange={(e) => HandleInputChange('exerciseInstructions', e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    maxLength={500}
                />

                {!validation.exerciseInstructions && (
                    <div className="mt-1 text-xs text-red-400">
                        Instructions must be 500 characters or less
                    </div>
                )}
            </div>

            {/* 
            <div>
                <label className="block text-sm font-medium text-gray-700">
                    Video Instruction (.mp4
                )</label>

                <input
                    type="file"
                    accept=".mp4"
                    onChange={(e) => HandleInputChange('exerciseVideo', e.target.files[0])}
                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />

                {!validation.exerciseVideo && (
                    <div className="mt-1 text-xs text-red-400">
                        Video must be 5MB or less
                    </div>
                )}
            </div> 
            */}

            {/* 
            <div>
                <label className="block text-sm font-medium text-gray-700">
                    Thumbnail (.png)
                </label>

                <input
                    type="file"
                    accept=".png"
                    onChange={(e) => HandleInputChange('exerciseThumbnail', e.target.files[0])}
                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />

                {!validation.exerciseThumbnail && (
                    <div className="mt-1 text-xs text-red-400">
                        Thumbnail must be 1MB or less
                    </div>
                )}
            </div> 
            */}

            <BrandColorButton
                text="Create Exercise"
                action={OnCreateExercise}
                isLoading={isSubmitting}
            />

            <OutlineButton
                text='Cancel'
                action={() => OnCancelButton()}
            />
        </div>
    );
}
